const reasons = [
  {
    icon: "ri-heart-line",
    title: "Wearable Manifesto",
    body: "A bag charm is the lowest-barrier wearable. No sizing, no commitment, no rules. Anyone can carry a charm — and in doing so, carry a statement.",
  },
  {
    icon: "ri-recycle-line",
    title: "Second Life Objects",
    body: "By converting textile waste into desirable accessories, we close the loop on discarded materials and create objects with emotional and material longevity.",
  },
  {
    icon: "ri-map-pin-line",
    title: "Visible Conversation",
    body: "Unlike clothing, a bag charm is always visible, always on display. It sparks conversations about waste, about craft, about what we choose to carry and show.",
  },
  {
    icon: "ri-scissors-line",
    title: "Craft in the Palm",
    body: "Each charm is made by hand — cut, sewn, stuffed, and assembled from real discarded garment pieces. The craft is visible, the process is present, the maker is remembered.",
  },
];

export default function WhyBagCharmsSection() {
  return (
    <section id="bag-charms" className="bg-[#F5F3EF] py-0">
      {/* Large hero image block */}
      <div className="w-full px-8 md:px-16 pt-24 md:pt-36">
        {/* Header */}
        <div className="flex items-center gap-4 mb-16">
          <span className="bg-[#1D3557] text-white font-mono text-[10px] uppercase tracking-[3px] px-3 py-1.5 rounded-full whitespace-nowrap">
            05 / 06
          </span>
          <div className="flex-1 h-[1px] bg-[#0D0D0D]/15" />
          <span className="font-mono text-[10px] text-[#0D0D0D]/30 uppercase tracking-[2px] whitespace-nowrap">
            Why Bag Charms
          </span>
        </div>
      </div>

      {/* Full-width rounded image */}
      <div className="mx-8 md:mx-16 rounded-3xl overflow-hidden relative" style={{ height: "80vh", minHeight: "500px" }}>
        <img
          src="https://static.readdy.ai/image/79bbd12133c9c5c423ad9964f66c8be6/4d19da2e8e5ca48c581f116c212afe33.png"
          alt="Bag charm wearable"
          className="w-full h-full object-cover object-center"
        />
        {/* Vignette */}
        <div className="absolute inset-0 bg-gradient-to-t from-[#0D0D0D]/60 via-transparent to-transparent" />

        {/* Overlay Text Container */}
        <div className="absolute bottom-10 left-10 max-w-lg">
          <div className="bg-white/15 backdrop-blur-md rounded-2xl p-8 border border-white/20">
            <p className="font-mono text-[10px] uppercase tracking-[3px] text-[#E63946] mb-3">
              WHY BAG CHARMS
            </p>
            <h2
              className="font-display text-white leading-tight mb-4"
              style={{ fontSize: "clamp(36px, 4vw, 56px)" }}
            >
              Wearable<br />Imperfection
            </h2>
            <p className="font-body text-white/80 text-base leading-relaxed">
              The bag charm is a radical democratic object. It asks nothing of the wearer&apos;s body, challenges nothing about their size or shape — and yet it makes the most powerful statement of all.
            </p>
          </div>
        </div>

        {/* Top right stat */}
        <div className="absolute top-10 right-10 text-right">
          <div className="bg-[#E63946] rounded-2xl px-6 py-4">
            <span className="font-display text-4xl text-white leading-none block">100%</span>
            <span className="font-mono text-[9px] text-white/80 uppercase tracking-[2px]">
              Textile Waste Materials
            </span>
          </div>
        </div>
      </div>

      {/* Four Reasons Grid */}
      <div className="w-full px-8 md:px-16 py-20">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {reasons.map((r, i) => (
            <div key={r.title} className="group">
              {/* Number */}
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 flex items-center justify-center rounded-full border-2 border-[#0D0D0D]/15 group-hover:border-[#E63946] group-hover:bg-[#E63946] transition-all duration-300">
                  <i className={`${r.icon} text-[#0D0D0D]/40 group-hover:text-white text-base transition-colors`} />
                </div>
                <div className="flex-1 h-[1px] bg-[#0D0D0D]/10" />
                <span className="font-mono text-[10px] text-[#0D0D0D]/20">{String(i + 1).padStart(2, "0")}</span>
              </div>
              <h3 className="font-body text-[#0D0D0D] text-lg font-semibold mb-3 leading-tight">
                {r.title}
              </h3>
              <p className="font-body text-[#2B2B2B]/55 text-sm leading-relaxed">
                {r.body}
              </p>
            </div>
          ))}
        </div>

        {/* Event Banner */}
        <div className="mt-16 bg-[#E63946] rounded-3xl px-10 py-10 flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[4px] text-white/60 mb-2">
              Exhibition Opening
            </p>
            <h3 className="font-display text-4xl md:text-5xl text-white leading-tight">
              26 MARCH 2026
            </h3>
          </div>
          <div className="w-[2px] h-16 bg-white/20 hidden md:block" />
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[3px] text-white/60 mb-2">Venue</p>
            <p className="font-body text-white text-xl font-semibold">Town House</p>
            <p className="font-body text-white/70 text-base">Kingston University, London</p>
          </div>
          <div className="w-[2px] h-16 bg-white/20 hidden md:block" />
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[3px] text-white/60 mb-2">In Collaboration</p>
            <p className="font-body text-white text-xl font-semibold">KAPDAA</p>
            <p className="font-body text-white/70 text-base">Sustainable Textile Innovation</p>
          </div>
          <div className="hidden md:flex items-center justify-center w-14 h-14">
            <i className="ri-arrow-right-line text-white text-2xl" />
          </div>
        </div>
      </div>
    </section>
  );
}
